import sessionStorage from 'redux-persist/lib/storage/session';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import createTransform from 'redux-persist/lib/createTransform';
import {
  CASE_LIST_PERSIST_KEY,
  CASE_LIST_PERSIST_STORAGE_KEY
} from '../../util/persistKeys';

/**
 * FIN-13485 — persist the Case Listing filters across a full page reload.
 *
 * The root store already runs redux-persist (configureStore.*.js) but its whitelist does
 * not include `cmCaseList`, so every reload reset the filters to `initialState.filters`
 * (30-day window, empty search, no department). Investigators are redirected back to the
 * listing after the STR/CSV flow and lost their filters each time.
 *
 * Only `filters` (and who they belong to) is persisted — never `list`, `isLoading`,
 * `selectedIds`, etc. — so a reload can not resurrect a stale list, a stuck spinner or
 * ghost selections.
 *
 * sessionStorage, not localStorage: the requirement is "keep the filters while I return to
 * the listing", scoped to the tab; it dies with the tab.
 *
 * Ownership. The auth cleanup (`clearAuthenticationStorage`) preserves this entry, so a
 * token refresh that fails after an idle gap — which logs the investigator out and back in —
 * no longer costs them their filters. What keeps the next person on the same tab from
 * inheriting them is done here, in the persist layer, synchronously:
 *
 *   - on the way OUT (write), `ownerStampTransform` stamps `filtersOwner` with the current
 *     identity — the username both login paths write to localStorage before the CM app boots;
 *   - on the way IN (rehydrate), `ownedFiltersReconciler` restores the filters only when the
 *     stored owner is the current identity; a different or unknown identity gets the defaults.
 *     An unowned blob (written before owners existed) is adopted by the identity present.
 *
 * Rehydration is started only once identity is known (index.js creates the persistor via
 * `startPersisting()` after Keycloak init has written the username), so there is no window in
 * which another user's filters are live in the store — nothing can fetch with them, nothing
 * can render them.
 *
 * autoMergeLevel2 merges the persisted `filters` object OVER the reducer defaults, so a
 * filter key added in a later release still gets its default when the stored blob predates it.
 */
export { CASE_LIST_PERSIST_KEY, CASE_LIST_PERSIST_STORAGE_KEY };
export const CASE_LIST_PERSIST_WHITELIST = ['filters', 'filtersOwner'];
export const CASE_LIST_PERSIST_VERSION = 1;

/** The identity both login paths put in localStorage before the CM app boots; null if none. */
export const currentIdentity = () => {
  try {
    const name = localStorage.getItem('userName');
    return name ? String(name) : null;
  } catch (_) {
    return null;
  }
};

const sameIdentity = (a, b) =>
  a != null && b != null && String(a) === String(b);

/**
 * Restore the filters only for their owner.
 *
 * inbound = what storage holds, original = the store's state before rehydrate,
 * reduced = the reducer's current state (defaults on a fresh boot).
 */
export const ownedFiltersReconciler =
  (identityOf = currentIdentity) =>
  (inboundState, originalState, reducedState, config) => {
    const merged = autoMergeLevel2(
      inboundState,
      originalState,
      reducedState,
      config
    );
    const identity = identityOf();
    const owner = inboundState?.filtersOwner ?? null;

    if (owner === null) {
      // unowned (written before owners existed): adopt, under whoever is here now
      return { ...merged, filtersOwner: identity };
    }
    if (sameIdentity(owner, identity)) {
      return { ...merged, filtersOwner: identity }; // the same person is back
    }
    // owned by someone else — or by someone, while nobody is known: never expose them
    return {
      ...merged,
      filters: reducedState.filters,
      filtersOwner: identity
    };
  };

/** Stamp the owner on the way to storage, from the identity — never from what the reducer holds. */
export const ownerStampTransform = (identityOf = currentIdentity) =>
  createTransform(
    (value, key) =>
      key === 'filtersOwner' ? identityOf() ?? value ?? null : value,
    undefined,
    { whitelist: ['filtersOwner'] }
  );

export const createCaseListPersistConfig = (
  storage = sessionStorage,
  identityOf = currentIdentity
) => ({
  key: CASE_LIST_PERSIST_KEY,
  storage,
  whitelist: CASE_LIST_PERSIST_WHITELIST,
  stateReconciler: ownedFiltersReconciler(identityOf),
  transforms: [ownerStampTransform(identityOf)],
  version: CASE_LIST_PERSIST_VERSION
});

export default createCaseListPersistConfig();
